/*
 * cl-switcher.js
 * --------------
 * Runtime for the self-contained Combined-Listing switcher (Habitat 1.6.1, no
 * native CL support). Pairs with snippets/cl-switcher.liquid.
 *
 * Problem: a Combined Listing's parent groups several CHILD products. Each value
 * of the "config" option (e.g. Configuration) belongs to a different child, and
 * children can have DIFFERENT option sets (some have Orientation, some don't).
 * The theme renders the parent as one flat multi-variant product, so it can't
 * resolve a variant when option sets differ and freezes on the default child
 * (URL/price/image stuck).
 *
 * Fix: this runtime is the SINGLE handler for every option change inside the
 * product picker (`<variant-selects>`). On any change it reads all currently
 * selected option values and decides the target:
 *
 *   1. CONFIG change — the changed combined-listing option value maps (via
 *      product_url) to a DIFFERENT child product: fetch that child's main-product
 *      section, extract its `.thb-product-detail`, and replace the live
 *      `.thb-product-detail` innerHTML in place. The child's section carries its
 *      OWN correct picker (incl. Orientation), price, gallery and add-to-cart
 *      form, so mixed-option families work.
 *
 *   2. WITHIN-PRODUCT change — Color/Orientation/etc. on the CURRENT child: the
 *      theme's native variant handling is broken on these combined pages (the
 *      mixed-in CL option breaks its variant matching, so image + price never
 *      swap). We resolve the newly-selected variant from the child's inline
 *      variant JSON, then section-render the CURRENT product at that variant
 *      (`?variant=<id>&section_id=<id>`) and swap `.thb-product-detail`, which
 *      brings the correct price + gallery slide.
 *
 * Shopify custom elements (product-slider, variant-selects) run
 * connectedCallback() when inserted, so the gallery + picker re-initialize
 * themselves after every swap.
 *
 * IMPORTANT: the picker (`<variant-selects>` and its inline variant JSON) lives
 * INSIDE `.thb-product-detail`, so a config swap replaces it with a different
 * child's picker. We therefore read the variant JSON and the current product URL
 * LIVE from the DOM on every change rather than caching them at init.
 *
 * Defensive: any failure falls back to plain navigation (window.location = url),
 * so the user always reaches the correct product, and the in-flight guard is
 * reset on every branch so the page never freezes.
 */
(function () {
  'use strict';

  var DETAIL_SELECTOR = '.thb-product-detail';
  var DATA_ID = 'cl-switcher-data';
  var BOUND_FLAG = 'clSwitcherBound';

  function readData() {
    var el = document.getElementById(DATA_ID);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  // Normalize a URL to its pathname so query params / origin differences don't
  // cause false "different product" or false "same product" decisions.
  function pathOf(url) {
    try {
      return new URL(url, window.location.origin).pathname.replace(/\/+$/, '');
    } catch (e) {
      // Best-effort: strip query/hash and trailing slash from a raw string.
      return String(url || '')
        .split('#')[0]
        .split('?')[0]
        .replace(/\/+$/, '');
    }
  }

  // Debug logging: off by default; enable in the console with
  // localStorage.setItem('cl-switcher-debug', '1') and reload.
  // Timestamps are seconds since page start.
  var DEBUG = (function () {
    try {
      return window.localStorage.getItem('cl-switcher-debug') === '1';
    } catch (e) {
      return false;
    }
  })();
  function dbg() {
    if (!DEBUG) return;
    var args = ['[cl-switcher]', (performance.now() / 1000).toFixed(2) + 's'];
    for (var i = 0; i < arguments.length; i++) args.push(arguments[i]);
    try {
      console.log.apply(console, args);
    } catch (e) {}
  }

  var data = readData();
  if (!data || !data.values || !data.values.length || !data.optionName) {
    return; // not a combined listing (or malformed) -> do nothing.
  }

  dbg(
    'boot:',
    data.optionName,
    data.values.length + ' values; mode:',
    typeof window.GloboSwatchConfig !== 'undefined' ? 'globo' : 'native'
  );

  // Tripwire: if ANYTHING triggers a real page navigation (which the native
  // in-page swap mode should never do), log it with a timestamp so an
  // unexpected "page loads 10s later" has a fingerprint in the console.
  window.addEventListener('pagehide', function () {
    dbg('PAGE UNLOADING — a full navigation/reload is happening NOW');
  });

  // Live runtime state. We track the current URL in JS rather than re-reading the
  // data node, because that node may live inside the swapped region and a
  // re-inserted <script> would not re-run anyway.
  var sectionId = data.sectionId;
  var optionName = data.optionName;
  var currentPath = pathOf(data.currentUrl);

  // Build a lookup from option-value title -> child url.
  function buildMap(values) {
    var map = {};
    for (var i = 0; i < values.length; i++) {
      if (values[i] && values[i].value != null) {
        map[String(values[i].value)] = values[i].url;
      }
    }
    return map;
  }
  var valueToUrl = buildMap(data.values);

  // Does this control belong to the combined-listing option?
  // Radios use name="Configuration"; selects use name="options[Configuration]".
  function isClOptionControl(name) {
    if (!name) return false;
    return name === optionName || name === 'options[' + optionName + ']';
  }

  // Pull the selected config value out of whatever control fired.
  function selectedValueFrom(target) {
    if (!target) return null;
    if (target.tagName === 'SELECT') return target.value;
    if (target.type === 'radio' && target.checked) return target.value;
    return null;
  }

  function navigateFallback(url) {
    dbg('FULL NAVIGATION fallback ->', url);
    window.location = url;
  }

  // Append a query param to a URL string (handles ?/& and existing keys cheaply;
  // good enough for our internal product URLs).
  function withParam(url, key, val) {
    return (
      url +
      (url.indexOf('?') === -1 ? '?' : '&') +
      key +
      '=' +
      encodeURIComponent(val)
    );
  }

  // The live picker element for the currently-rendered child. Lives INSIDE
  // .thb-product-detail, so it changes identity after every config swap.
  function liveVariantSelects() {
    return document.querySelector('variant-selects');
  }

  // Current product URL, read LIVE: prefer the live picker's data-url, fall back
  // to our maintained currentPath. This stays correct after a config swap.
  function currentProductUrl() {
    var vs = liveVariantSelects();
    var u = vs && vs.getAttribute('data-url');
    return u || currentPath;
  }

  // Parse the current child's inline variant JSON (array of variant objects).
  function liveVariants() {
    var vs = liveVariantSelects();
    if (!vs) return null;
    var script = vs.querySelector('script[type="application/json"]');
    if (!script) return null;
    try {
      var arr = JSON.parse(script.textContent);
      return Array.isArray(arr) ? arr : null;
    } catch (e) {
      return null;
    }
  }

  // Read every currently-selected option value from the live picker as a flat
  // list of values (we don't need the names: variant.options is positional and
  // its value vocabulary is distinct across options on these products).
  function selectedOptionValues() {
    var vs = liveVariantSelects();
    if (!vs) return [];
    var out = [];
    // Radios: one checked per option group.
    var radios = vs.querySelectorAll('input[type="radio"]:checked');
    for (var i = 0; i < radios.length; i++) out.push(radios[i].value);
    // Selects: each contributes its current value.
    var selects = vs.querySelectorAll('select');
    for (var j = 0; j < selects.length; j++) {
      if (selects[j].value != null && selects[j].value !== '') {
        out.push(selects[j].value);
      }
    }
    return out;
  }

  // Find the variant whose option values exactly match the selected set. The CL
  // config value is identical across a child's variants, so matching all
  // selected values (Config + Color + Orientation) resolves a single variant.
  // Returns the variant id, or null if none/ambiguous matches.
  function resolveVariant(variants, selectedValues) {
    if (!variants || !selectedValues.length) return null;
    var found = null;
    for (var i = 0; i < variants.length; i++) {
      var opts = variants[i] && variants[i].options;
      if (!opts || opts.length !== selectedValues.length) continue;
      var ok = true;
      for (var k = 0; k < selectedValues.length; k++) {
        if (opts.indexOf(selectedValues[k]) === -1) {
          ok = false;
          break;
        }
      }
      if (ok) {
        if (found != null && found.id !== variants[i].id) return null; // ambiguous
        found = variants[i];
      }
    }
    return found;
  }

  // After a within-product swap, jump the gallery to the selected variant's OWN
  // image. The section's lead slide is shared across a product's variants (e.g.
  // Left/Right Facing share the same first photo), so without this the visible
  // image wouldn't change. Waits for Flickity to (re)initialize after the swap.
  function selectGallerySlide(detailEl, mediaId) {
    if (!mediaId) return;
    var slider = detailEl.querySelector('.product-images');
    if (!slider) return;
    var slideSel = '[id="Slide-' + sectionId + '-' + mediaId + '"]';
    var attempts = 0;
    function tick() {
      attempts++;
      var flkty =
        typeof Flickity !== 'undefined' && Flickity.data ? Flickity.data(slider) : null;
      var slide = slider.querySelector(slideSel);
      if (flkty && slide) {
        try {
          flkty.selectCell(slide);
        } catch (e) {}
        return;
      }
      if (attempts < 30) window.requestAnimationFrame(tick);
    }
    window.requestAnimationFrame(tick);
  }

  // Hide option values that don't exist for the CURRENT child. The picker renders
  // the UNION of all values across the combined listing (e.g. Orientation =
  // Right/Left/Symmetrical), but a given child may only offer some. We never touch
  // the combined-listing config option — its other values are valid siblings
  // reachable via product_url.
  function pruneUnavailableOptions() {
    var vs = liveVariantSelects();
    var variants = liveVariants();
    if (!vs || !variants) return;
    var available = {};
    for (var i = 0; i < variants.length; i++) {
      var opts = variants[i] && variants[i].options;
      if (!opts) continue;
      for (var k = 0; k < opts.length; k++) available[opts[k]] = true;
    }
    var radios = vs.querySelectorAll('input[type="radio"]');
    for (var r = 0; r < radios.length; r++) {
      var radio = radios[r];
      if (isClOptionControl(radio.name)) continue; // leave the config option intact
      var ok = available[radio.value] === true;
      radio.disabled = !ok;
      var label = vs.querySelector('label[for="' + radio.id + '"]');
      if (label) label.style.display = ok ? '' : 'none';
    }
  }

  var swapInFlight = false;

  // Core swap: section-render `url` (already carrying any ?variant=) and replace
  // the live .thb-product-detail innerHTML. `historyUrl` is pushed/replaced into
  // the address bar; `usePush` chooses pushState (config->child) vs replaceState
  // (within-product variant). Custom elements re-init via connectedCallback.
  function swapDetail(url, historyUrl, usePush, mediaId) {
    if (swapInFlight) {
      dbg('swap: IGNORED (another swap in flight) ->', url);
      return;
    }
    var liveDetail = document.querySelector(DETAIL_SELECTOR);
    if (!liveDetail) {
      dbg('swap: no live', DETAIL_SELECTOR, 'on page');
      navigateFallback(historyUrl || url);
      return;
    }
    swapInFlight = true;
    var t0 = performance.now();
    dbg('swap: start ->', url);

    // Immediate visual feedback: without it a contended fetch reads as
    // "frozen" rather than "loading".
    document.documentElement.style.cursor = 'progress';
    liveDetail.style.opacity = '0.55';
    liveDetail.style.pointerEvents = 'none';
    function clearBusy() {
      document.documentElement.style.cursor = '';
      liveDetail.style.opacity = '';
      liveDetail.style.pointerEvents = '';
    }

    // Source priority: (1) a RESOLVED warm cache entry — instant. (2) a warm
    // still in flight — it may be starved behind the page's image traffic
    // (observed: 14.8s for 198KB), so abort the competing warms and race it
    // against a fresh HIGH-priority fetch; first result wins. (3) no warm —
    // straight high-priority fetch. Only config swaps (usePush) are
    // cacheable; ?variant= re-renders are variant-specific.
    var sectionUrl = withParam(url, 'section_id', sectionId);
    var key = pathOf(url);
    var primary;
    if (usePush && sectionCache[key] && warmSettled[key]) {
      dbg('swap: using warmed section cache');
      primary = sectionCache[key];
    } else if (usePush && sectionCache[key]) {
      dbg('swap: warm still in flight -> escalating with high-priority fetch');
      abortOtherWarms(key);
      primary = Promise.race([
        sectionCache[key],
        fetchDetail(sectionUrl, 'click escalation', { priority: 'high' })
      ]);
    } else {
      primary = fetchDetail(sectionUrl, 'section render', { priority: 'high' });
    }

    primary
      .then(function (detailHtml) {
        if (detailHtml) return detailHtml;
        // Fallback: fetch the full page and extract the detail container.
        dbg('swap: section render had no detail; fetching full page');
        return fetchDetail(url, 'full page', { priority: 'high' });
      })
      .then(function (detailHtml) {
        if (!detailHtml) {
          throw new Error('no .thb-product-detail in response');
        }
        liveDetail.innerHTML = detailHtml;
        currentPath = pathOf(url);
        dbg('swap: DOM swapped in', Math.round(performance.now() - t0) + 'ms');

        // Jump the gallery to the variant's own image (within-product changes only;
        // config swaps pass no mediaId since the child's lead slide is already correct).
        selectGallerySlide(liveDetail, mediaId);

        // Hide option values not offered by the now-current child, and
        // re-tag the freshly swapped-in picker's config fieldset for the
        // grid styling.
        pruneUnavailableOptions();
        tagConfigFieldset();

        // Update browser URL so refresh / share lands on the right state.
        try {
          if (usePush) {
            window.history.pushState({ clSwitcher: true }, '', historyUrl);
          } else {
            window.history.replaceState({ clSwitcher: true }, '', historyUrl);
          }
        } catch (e) {
          /* can throw on cross-origin; ignore, swap already done */
        }

        // Update document title from the freshly-swapped picker if available.
        updateTitleFrom(liveDetail);

        swapInFlight = false;
        clearBusy();
        dbg('swap: complete in', Math.round(performance.now() - t0) + 'ms');

        // Shopify's product_url can carry ?variant= (the matching variant on
        // the sibling, e.g. same color). Warmed sections render the DEFAULT
        // variant — if the requested one differs from what the swapped-in
        // form selected, re-render at it (only fires when they differ).
        if (usePush) {
          var vm = /[?&]variant=(\d+)/.exec(url);
          var idInput =
            vm && liveDetail.querySelector('form[action*="/cart/add"] [name="id"]');
          if (vm && idInput && String(idInput.value) !== vm[1]) {
            dbg('swap: cache showed default variant; re-rendering at', vm[1]);
            var vs2 = liveVariants();
            var want = null;
            for (var vi = 0; vs2 && vi < vs2.length; vi++) {
              if (String(vs2[vi].id) === vm[1]) want = vs2[vi];
            }
            var wantMedia = want && want.featured_media && want.featured_media.id;
            setTimeout(function () {
              swapToVariant(currentProductUrl(), vm[1], wantMedia);
            }, 0);
          }
        }

        // After a config swap the previous product became a sibling — top up
        // the cache so switching back is instant too. Deferred to idle so it
        // doesn't compete with the freshly swapped-in gallery's image loads
        // (observed: an immediate top-up fetch took 10s for that reason).
        if (usePush) {
          if (window.requestIdleCallback) {
            window.requestIdleCallback(warmAllSiblings, { timeout: 6000 });
          } else {
            setTimeout(warmAllSiblings, 2500);
          }
        }
      })
      .catch(function (err) {
        swapInFlight = false;
        clearBusy();
        dbg('swap: FAILED after', Math.round(performance.now() - t0) + 'ms:',
          err && err.message);
        navigateFallback(historyUrl || url); // always reach the right state.
      });
  }

  // CONFIG change: swap to a DIFFERENT child product (pushState the child URL).
  function swapToChild(url) {
    swapDetail(url, url, true);
  }

  // WITHIN-PRODUCT change: re-render the CURRENT product at `variantId`
  // (replaceState a ?variant= URL so color/orientation clicks don't spam history).
  function swapToVariant(productUrl, variantId, mediaId) {
    var variantUrl = withParam(productUrl, 'variant', variantId);
    swapDetail(variantUrl, variantUrl, false, mediaId);
  }

  // Fetch a URL and return the innerHTML of its `.thb-product-detail`, or null.
  // opts.priority: 'low' for background warms (never compete with the page),
  // 'high' for click-time fetches (jump ahead of image traffic). opts.signal
  // aborts a warm the moment a click needs the bandwidth elsewhere.
  function fetchDetail(fetchUrl, label, opts) {
    var t0 = performance.now();
    var init = { headers: { 'X-Requested-With': 'XMLHttpRequest' } };
    if (opts && opts.priority) init.priority = opts.priority;
    if (opts && opts.signal) init.signal = opts.signal;
    return fetch(fetchUrl, init)
      .then(function (resp) {
        if (!resp.ok) throw new Error('HTTP ' + resp.status);
        return resp.text();
      })
      .then(function (text) {
        var doc = new DOMParser().parseFromString(text, 'text/html');
        var detail = doc.querySelector(DETAIL_SELECTOR);
        dbg(
          'fetch (' + (label || 'detail') + '):',
          Math.round(performance.now() - t0) + 'ms,',
          Math.round(text.length / 1024) + 'KB,',
          detail ? 'detail found' : 'NO detail in response'
        );
        return detail ? detail.innerHTML : null;
      });
  }

  /* ---- Sibling section warm-up (native mode) ----------------------------
   * First-click config swaps were slow (observed: an 11.5s section fetch)
   * because the click lands while the page is still downloading its own
   * megabytes of images/app scripts and our fetch queues behind them. So
   * once the page has finished loading and gone idle, fetch every sibling's
   * main-product section (~170KB each, strictly one at a time) into an
   * in-memory cache. A config click then swaps instantly from cache; a
   * click during warm-up awaits the already-in-flight request instead of
   * starting a new one.
   */
  var sectionCache = {}; // pathOf(sibling url) -> Promise<detailHtml|null>
  var warmSettled = {}; // key -> true once its promise has resolved
  var warmControllers = {}; // key -> AbortController while the warm is in flight
  var warmQueueRunning = false;

  // A click needs the network for ONE sibling; cancel the other in-flight
  // warms so they stop competing for bandwidth (they re-warm afterwards via
  // the post-swap top-up).
  function abortOtherWarms(exceptKey) {
    for (var key in warmControllers) {
      if (key === exceptKey) continue;
      try {
        warmControllers[key].abort();
      } catch (e) {}
      dbg('warm: aborted', key, 'to prioritize the click');
    }
  }

  // The DOM swap is ~40ms from cache, but the swapped-in gallery references
  // the sibling's full-size photos, which only start downloading at swap
  // time — that image wait is what reads as "slow". Preload the hero image
  // (first gallery slide) while warming, so the photo paints instantly.
  function preloadHeroImage(detailHtml, key) {
    try {
      var doc = new DOMParser().parseFromString(detailHtml, 'text/html');
      var img =
        doc.querySelector('.product-images img') || doc.querySelector('img');
      if (!img) return;
      var pre = new Image();
      var sizes = img.getAttribute('sizes');
      var srcset = img.getAttribute('srcset') || img.getAttribute('data-srcset');
      var src = img.getAttribute('src') || img.getAttribute('data-src');
      if (sizes) pre.sizes = sizes;
      if (srcset) pre.srcset = srcset;
      if (src) pre.src = src;
      if (srcset || src) dbg('warm: preloading hero image for', key);
    } catch (e) {}
  }

  function warmSibling(url) {
    var key = pathOf(url);
    if (sectionCache[key]) return sectionCache[key];
    var controller =
      typeof AbortController !== 'undefined' ? new AbortController() : null;
    if (controller) warmControllers[key] = controller;
    sectionCache[key] = fetchDetail(
      withParam(url, 'section_id', sectionId),
      'warm ' + key,
      { priority: 'low', signal: controller && controller.signal }
    )
      .then(function (html) {
        warmSettled[key] = true;
        delete warmControllers[key];
        if (html) preloadHeroImage(html, key);
        return html;
      })
      .catch(function () {
        delete sectionCache[key]; // failed/aborted warm: allow a re-warm
        delete warmControllers[key];
        return null;
      });
    return sectionCache[key];
  }

  function warmAllSiblings() {
    if (warmQueueRunning) return; // one serial queue at a time
    var queue = [];
    for (var i = 0; i < data.values.length; i++) {
      var val = data.values[i];
      if (!val.url) continue;
      var key = pathOf(val.url);
      if (key === currentPath || sectionCache[key]) continue;
      queue.push(val.url);
    }
    if (!queue.length) return;
    warmQueueRunning = true;
    dbg('warm: caching', queue.length, 'sibling sections (serial)');
    (function next() {
      var u = queue.shift();
      if (!u) {
        warmQueueRunning = false;
        dbg('warm: all siblings cached');
        return;
      }
      warmSibling(u).then(function () {
        setTimeout(next, 200); // breathe between fetches
      });
    })();
  }

  function scheduleWarm() {
    if (typeof window.GloboSwatchConfig !== 'undefined') return; // globo mode navigates; no section cache
    function idle() {
      if (window.requestIdleCallback) {
        window.requestIdleCallback(warmAllSiblings, { timeout: 8000 });
      } else {
        setTimeout(warmAllSiblings, 3000);
      }
    }
    if (document.readyState === 'complete') {
      idle();
    } else {
      window.addEventListener('load', idle, { once: true });
    }
  }

  function updateTitleFrom(detailEl) {
    var titleEl = detailEl.querySelector('.product-title');
    if (titleEl && titleEl.textContent) {
      var t = titleEl.textContent.trim();
      if (t) document.title = t;
    }
  }

  /* ---- Globo Color Swatch coexistence ------------------------------------
   * When the Globo Color Swatch app embed is active it hides the theme's
   * native option rows and rebuilds the picker from the product's OWN
   * variants — so the combined-listing option (whose sibling values exist
   * only as product_url links, not variants) disappears entirely. We
   * re-inject that row into Globo's container using Globo's own markup and
   * classes (its CSS then styles it like the other rows), and navigate to
   * the sibling product on click. Full navigation, not a section swap:
   * Globo re-initializes naturally on the next page load, so the picker
   * styling stays consistent. While Globo is active it owns all
   * within-product changes (price/image swaps), so handleChange stands down.
   */
  var GLOBO_CONTAINER_SELECTOR = '.globo-swatch-product-detail';
  var CL_ROW_CLASS = 'cl-switcher-globo-row';

  function globoContainer() {
    return document.querySelector(GLOBO_CONTAINER_SELECTOR);
  }

  function escapeHtml(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  // Sibling warm-up = document prefetch on hover/touch intent ONLY.
  // Prerendering was tried and abandoned: on this site it NEVER activated
  // (third-party scripts cancel it — every boot logged "not prerendered")
  // while its full-page background loads congested the network so badly
  // that real navigations took 10s+ (observed in the debug logs; a click
  // made after warm-up traffic drained booted in 0.33s). Sibling pages
  // share almost all subresources — theme/app assets are cached after the
  // first page view — so the HTML document is the only real per-page cost,
  // and one small prefetch on hover cannot congest anything.
  var prefetched = {};

  function prefetchOnIntent(url) {
    if (!url || prefetched[url]) return;
    prefetched[url] = true;
    var link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = url;
    link.as = 'document';
    document.head.appendChild(link);
    dbg('prefetch (intent):', url);
  }

  // Long config labels ("4-Piece Double Chaise Sectional") make auto-width
  // pills ragged. Lay the config row out as an equal-width 2-column grid —
  // every pill the same width and height, text centered. The second block
  // styles the NATIVE picker (Globo off) to the look the client approved
  // from Globo: thin teal pills (--blue_bg_color, the Save-badge teal)
  // instead of the theme's navy --color-accent. Scoped to combined-listing
  // pages only (this script no-ops elsewhere).
  var CL_STYLE_ID = 'cl-switcher-globo-style';
  var CL_FIELDSET_CLASS = 'cl-config-fieldset';
  function ensureRowStyle() {
    if (document.getElementById(CL_STYLE_ID)) return;
    var st = document.createElement('style');
    st.id = CL_STYLE_ID;
    st.textContent =
      /* injected row in Globo mode */
      '.' + CL_ROW_CLASS + ' ul.value{display:grid;' +
      'grid-template-columns:repeat(2,1fr);gap:8px;' +
      'padding:0;margin:0 0 12px;list-style:none;}' +
      '.' + CL_ROW_CLASS + ' ul.value li.select-option{margin:0;float:none;' +
      'width:auto;display:flex;}' +
      '.' + CL_ROW_CLASS + ' ul.value li.select-option label.swatch-anchor{' +
      'display:flex;align-items:center;justify-content:center;' +
      'width:100%;min-height:100%;box-sizing:border-box;text-align:center;' +
      'margin:0;white-space:normal;line-height:1.3;}' +
      /* native picker, Globo-look teal pills (all block-type option rows).
         The redesign layers several higher-specificity pill rules (capsule
         radius, navy accent) plus a 62.5% root font hack, so these use px
         sizes and !important to settle the cascade decisively. */
      '.thb-product-detail .variations .product-form__input--block ' +
      'input[type="radio"]+label{' +
      'border:1px solid var(--blue_bg_color,#4B828F)!important;' +
      'color:var(--blue_bg_color,#4B828F)!important;' +
      'background:#fff!important;' +
      'border-radius:4px!important;' +
      'height:auto!important;min-height:34px!important;' +
      /* asymmetric on purpose: Proza Libre sits high in its line box, so
         equal padding reads top-heavy — this shifts the text down ~1px
         (empirically calibrated: 6/6 reads high, 8/4 reads low) */
      'padding:7px 14px 5px!important;' +
      'font-size:13px!important;line-height:1.3!important;}' +
      '.thb-product-detail .variations .product-form__input--block ' +
      'input[type="radio"]:checked+label{' +
      'background:var(--blue_bg_color,#4B828F)!important;' +
      'color:#fff!important;}' +
      '.thb-product-detail .variations .product-form__input--block ' +
      'input[type="radio"]:not(:checked):hover+label{' +
      'background:rgba(75,130,143,.08)!important;}' +
      /* native config fieldset: aligned 2-column grid */
      '.' + CL_FIELDSET_CLASS + '{display:grid;' +
      'grid-template-columns:repeat(2,1fr);gap:8px;}' +
      '.' + CL_FIELDSET_CLASS + ' legend{grid-column:1/-1;}' +
      '.' + CL_FIELDSET_CLASS + ' input[type="radio"]{' +
      'position:absolute;opacity:0;pointer-events:none;}' +
      '.' + CL_FIELDSET_CLASS + ' input[type="radio"]+label{' +
      'width:100%;height:100%;margin:0;box-sizing:border-box;' +
      'white-space:normal;text-align:center;}' +
      /* desktop: ~30% narrower pills so long labels wrap to two lines,
         matching the approved mobile look; mobile keeps full width */
      '@media only screen and (min-width:768px){' +
      '.' + CL_FIELDSET_CLASS + '{max-width:70%;}' +
      '.' + CL_ROW_CLASS + ' ul.value{max-width:70%;}' +
      '}';
    document.head.appendChild(st);
  }

  // Tag the native picker's combined-listing fieldset so the grid CSS above
  // can target it. Re-run after every section swap (the picker is replaced).
  function tagConfigFieldset() {
    var vs = liveVariantSelects();
    if (!vs) return;
    var radios = vs.querySelectorAll('input[type="radio"]');
    for (var r = 0; r < radios.length; r++) {
      if (isClOptionControl(radios[r].name)) {
        var fs = radios[r].closest ? radios[r].closest('fieldset') : null;
        if (fs && fs.className.indexOf(CL_FIELDSET_CLASS) === -1) {
          fs.className += ' ' + CL_FIELDSET_CLASS;
        }
        return;
      }
    }
  }

  function injectGloboConfigRow() {
    var container = globoContainer();
    if (!container) return false;
    var list = container.querySelector('.globo-swatch-list') || container;
    ensureRowStyle();
    if (list.querySelector('.' + CL_ROW_CLASS)) return true; // already injected

    // Drop any row Globo itself rendered for the config option — it was built
    // from this child's variants only, so it lacks the sibling values.
    var legends = list.querySelectorAll('legend.name-option');
    for (var i = 0; i < legends.length; i++) {
      if (legends[i].getAttribute('data-name') === optionName) {
        var stale = legends[i].closest('.swatch--gl');
        if (stale && stale.parentNode) stale.parentNode.removeChild(stale);
      }
    }

    // Mirror Globo's text-button markup. Borrow the class list from a label
    // Globo already rendered so the merchant's configured button style
    // (corner/size/text/effects) applies to our row too.
    var sample = list.querySelector('label.swatch-anchor.globo-style--button');
    var labelClass = sample
      ? sample.className
      : 'swatch-anchor swatch-other globo-style--button';

    var current = '';
    for (var c = 0; c < data.values.length; c++) {
      if (data.values[c].current) current = data.values[c].value;
    }

    var html =
      '<legend class="name-option" data-name="' + escapeHtml(optionName) + '">' +
      escapeHtml(optionName) +
      '<span class="name-variant">: ' + escapeHtml(current) + '</span></legend>';
    html += '<ul class="value">';
    for (var v = 0; v < data.values.length; v++) {
      var val = data.values[v];
      var id = 'cl-config-' + v;
      html +=
        '<li class="select-option available" data-value="' + escapeHtml(val.value) + '">' +
        '<input id="' + id + '" type="radio" name="cl-config" value="' +
        escapeHtml(val.value) + '"' + (val.current ? ' checked' : '') +
        ' aria-label="' + escapeHtml(val.value) + '" />' +
        '<label for="' + id + '" class="' + labelClass + '" style="display: block;">' +
        escapeHtml(val.value) + '</label></li>';
    }
    html += '</ul>';

    var row = document.createElement('div');
    row.className = 'swatch--gl clearfix ' + CL_ROW_CLASS;
    row.innerHTML = html;
    list.insertBefore(row, list.firstChild);
    dbg('globo: config row injected (' + data.values.length + ' values)');

    row.addEventListener('change', function (e) {
      e.stopPropagation(); // keep Globo's own handlers away from our row
      var url = valueToUrl[String(e.target.value)];
      if (url && pathOf(url) !== currentPath) {
        dbg('config click:', e.target.value, '-> navigating to', url);
        // Immediate feedback so a slower navigation doesn't feel stuck.
        document.documentElement.style.cursor = 'progress';
        row.style.opacity = '0.6';
        row.style.pointerEvents = 'none'; // no double-clicks mid-navigation
        navigateFallback(url);
      }
    });

    // Warm the hovered/touched sibling's document only.
    function intent(e) {
      var li = e.target && e.target.closest ? e.target.closest('li[data-value]') : null;
      if (!li) return;
      var url = valueToUrl[String(li.getAttribute('data-value'))];
      if (url && pathOf(url) !== currentPath) prefetchOnIntent(url);
    }
    row.addEventListener('pointerover', intent, { passive: true });
    row.addEventListener('touchstart', intent, { passive: true });
    return true;
  }

  // Globo renders the UNION of option values (its embed serializes the
  // combined listing's options_with_values), so values belonging only to
  // sibling products (e.g. a Symmetrical orientation another config offers)
  // show up as dead pills. Hide any value the CURRENT product's variants
  // don't actually offer — never touching our injected config row.
  function pruneGloboOptions() {
    var globo = globoContainer();
    var variants = liveVariants();
    if (!globo || !variants) return;
    var available = {};
    for (var i = 0; i < variants.length; i++) {
      var opts = variants[i] && variants[i].options;
      if (!opts) continue;
      for (var k = 0; k < opts.length; k++) available[opts[k]] = true;
    }
    var rows = globo.querySelectorAll('.swatch--gl');
    for (var r = 0; r < rows.length; r++) {
      var row = rows[r];
      if (row.className.indexOf(CL_ROW_CLASS) !== -1) continue;
      var legend = row.querySelector('legend.name-option');
      if (legend && legend.getAttribute('data-name') === optionName) continue;
      var items = row.querySelectorAll('li.select-option');
      for (var j = 0; j < items.length; j++) {
        var v = items[j].getAttribute('data-value');
        var show = available[v] === true;
        if (!show && items[j].style.display !== 'none') {
          dbg('prune: hiding sibling-only value', JSON.stringify(v));
        }
        items[j].style.display = show ? '' : 'none';
      }
    }
  }

  function globoSync() {
    var ok = injectGloboConfigRow();
    if (ok) pruneGloboOptions();
    return ok;
  }

  // Globo dispatches this after every render of its container (which wipes
  // our row via innerHTML), so re-inject + re-prune on each one.
  window.addEventListener('Globo.swatch.loaded', globoSync);

  // Cover the race where Globo rendered before this script ran (both load
  // async). Poll briefly only when the embed's config global is present.
  if (typeof window.GloboSwatchConfig !== 'undefined') {
    var globoTries = 0;
    (function tryInject() {
      if (globoSync() || ++globoTries > 40) return;
      setTimeout(tryInject, 250);
    })();
  }

  // Within-product changes in Globo mode. Globo's own variant handling
  // doesn't update this theme (its price/image selectors don't match), so
  // when a Globo swatch changes we resolve the variant ourselves and apply
  // it surgically — WITHOUT swapping .thb-product-detail, which would
  // destroy Globo's rendered picker: update the cart forms' variant id,
  // refresh only the theme's price node (stable id "price-<sectionId>") via
  // a section render, jump the gallery to the variant's image, and
  // replaceState the ?variant= URL.
  function globoSelectedValues(globo) {
    var out = [];
    var radios = globo.querySelectorAll('input[type="radio"]:checked');
    for (var i = 0; i < radios.length; i++) {
      var li = radios[i].closest ? radios[i].closest('li.select-option') : null;
      if (li && li.style.display === 'none') continue; // pruned value
      out.push(radios[i].value);
    }
    return out;
  }

  function applyVariantInPlace(variant) {
    var t0 = performance.now();
    dbg('variant: applying', variant.id, variant.title || '');
    var idInputs = document.querySelectorAll('form[action*="/cart/add"] [name="id"]');
    for (var i = 0; i < idInputs.length; i++) idInputs[i].value = variant.id;

    var detail = document.querySelector(DETAIL_SELECTOR);
    var mediaId = variant.featured_media && variant.featured_media.id;
    if (detail && mediaId) selectGallerySlide(detail, mediaId);

    var priceEl = document.getElementById('price-' + sectionId);
    if (priceEl) {
      var url = withParam(
        withParam(currentProductUrl(), 'variant', variant.id),
        'section_id',
        sectionId
      );
      fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then(function (resp) {
          if (!resp.ok) throw new Error('HTTP ' + resp.status);
          return resp.text();
        })
        .then(function (text) {
          var doc = new DOMParser().parseFromString(text, 'text/html');
          var fresh = doc.getElementById('price-' + sectionId);
          if (fresh) priceEl.innerHTML = fresh.innerHTML;
          dbg('variant: price refreshed in', Math.round(performance.now() - t0) + 'ms');
        })
        .catch(function (err) {
          /* price refresh is cosmetic; form id + gallery already updated */
          dbg('variant: price refresh failed:', err && err.message);
        });
    }

    try {
      window.history.replaceState(
        { clSwitcher: true },
        '',
        withParam(currentProductUrl(), 'variant', variant.id)
      );
    } catch (e) {}
  }

  // Single dispatcher for ALL option changes inside the product picker.
  function handleChange(e) {
    var target = e.target;
    // Globo mode: config changes are handled by the injected row's own
    // listener (it stops propagation); Globo swatch changes (Color,
    // Orientation, ...) are resolved and applied in place here. Everything
    // else — including the hidden native picker — is ignored.
    var globo = globoContainer();
    if (globo) {
      if (!target || !target.name || target.name.indexOf('globo-option-') !== 0) return;
      var gvariant = resolveVariant(liveVariants(), globoSelectedValues(globo));
      if (gvariant) applyVariantInPlace(gvariant);
      return;
    }
    // Scope to the live picker by DOM origin, NOT by name: Color/Orientation
    // names aren't in cl-switcher-data, and this keeps unrelated page controls
    // (newsletter, quantity, search...) from triggering spurious resolution.
    if (!target || typeof target.closest !== 'function') return;
    if (!target.closest('variant-selects')) return;
    if (swapInFlight) {
      dbg('change: IGNORED (swap in flight):', target.name, '=', target.value);
      return;
    }

    dbg('change:', target.name, '=', target.value);

    // (1) CONFIG change: the changed CL-option control maps to a DIFFERENT child.
    if (isClOptionControl(target.name)) {
      var value = selectedValueFrom(target);
      if (value != null) {
        var url = valueToUrl[String(value)];
        if (url && pathOf(url) !== currentPath) {
          dbg('route: config -> sibling child', url);
          swapToChild(url);
          return;
        }
        // Same-product CL value (e.g. re-selecting current config): fall through
        // to within-product resolution, which harmlessly re-resolves it.
      }
    }

    // (2) WITHIN-PRODUCT change: resolve the newly-selected variant of the
    // CURRENT product and section-render it. Read variants + URL LIVE so this
    // stays correct after a prior config swap replaced the picker.
    var variants = liveVariants();
    var variant = resolveVariant(variants, selectedOptionValues());
    if (!variant) {
      dbg('route: no unique variant match — leaving to the theme');
      return; // no/ambiguous match -> let the theme handle it.
    }

    dbg('route: within-product -> variant', variant.id);
    var mediaId = variant.featured_media && variant.featured_media.id;
    swapToVariant(currentProductUrl(), variant.id, mediaId);
  }

  // Delegate on a stable ancestor that is NOT replaced. The data/script render
  // outside .thb-product-detail, but to be safe we delegate on document so the
  // listener survives every innerHTML swap and re-binds nothing.
  function bind() {
    if (document[BOUND_FLAG]) return;
    document[BOUND_FLAG] = true;
    document.addEventListener('change', handleChange, false);
    // Hover/touch on a native config pill warms that sibling immediately —
    // covers clicks that beat the idle warm-up.
    document.addEventListener(
      'pointerover',
      function (e) {
        var label = e.target && e.target.closest ? e.target.closest('label[for]') : null;
        if (!label || !label.closest('variant-selects')) return;
        var input = document.getElementById(label.getAttribute('for'));
        if (!input || !isClOptionControl(input.name)) return;
        var url = valueToUrl[String(input.value)];
        if (url && pathOf(url) !== currentPath) warmSibling(url);
      },
      { passive: true }
    );
  }

  bind();
  pruneUnavailableOptions(); // prune on initial load too
  ensureRowStyle(); // teal pills + config grid (native mode styling)
  tagConfigFieldset();
  scheduleWarm(); // cache sibling sections once the page is loaded + idle
})();
